import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/components/ListingCard";

function sameUserId(a, b) {
  const left = a == null ? "" : String(a).trim();
  const right = b == null ? "" : String(b).trim();
  return Boolean(left) && Boolean(right) && left === right;
}

function listingSellerId(listing) {
  return listing?.seller_id || listing?.seller?.id || null;
}

/**
 * Contact CTA — plain overlay (no Radix). Prefer instant send when
 * `instant` is true (shop Contacter) to avoid fragile modal stacks on mobile Safari.
 */
export function ContactSellerDialog({
  listing,
  className = "",
  label = "Contacter",
  testId = "detail-contact",
  variant = "outline",
  buttonClassName = "",
  /** Skip compose UI: create conversation with default message and open thread. */
  instant = false,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!listing?.id) return null;

  const sellerId = listingSellerId(listing);
  const defaultMsg = `Bonjour, je suis intéressé(e) par votre annonce "${listing.title || ""}". Est-elle toujours disponible ?`;

  const openThread = (convId) => {
    setOpen(false);
    navigate(`/messages/${convId}`);
  };

  /** Instant Contacter: greet only on a brand-new thread to avoid spam on re-click. */
  const startInstant = async () => {
    const { data } = await api.post("/conversations", { listing_id: listing.id });
    if (data.created) {
      await api.post(`/conversations/${data.id}/messages`, { text: defaultMsg });
      toast.success("Message envoyé");
    } else {
      toast.success("Conversation ouverte");
    }
    openThread(data.id);
  };

  /** Modal Contacter: always send the composed text (even if a thread already exists). */
  const submitComposed = async (message) => {
    const { data } = await api.post("/conversations", {
      listing_id: listing.id,
      initial_message: message,
    });
    toast.success(data.message_sent ? "Message envoyé" : "Conversation ouverte");
    openThread(data.id);
  };

  const start = async (e) => {
    e?.preventDefault();
    e?.stopPropagation?.();
    if (!user) {
      toast.info("Connectez-vous pour contacter le vendeur");
      navigate(`/connexion?next=${encodeURIComponent(location.pathname)}`);
      return;
    }
    if (sameUserId(user?.id, sellerId)) {
      toast.info("C'est votre propre annonce. Vous ne pouvez pas vous contacter vous-même.");
      return;
    }
    if (instant) {
      setBusy(true);
      try {
        await startInstant();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setBusy(false);
      }
      return;
    }
    setText(defaultMsg);
    setOpen(true);
  };

  const submit = async () => {
    if (sameUserId(user?.id, sellerId)) {
      toast.error("Vous ne pouvez pas vous contacter vous-même");
      return;
    }
    setBusy(true);
    try {
      await submitComposed(text);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const defaultOutline =
    "w-full h-11 min-h-11 border-2 border-matpro-anthracite text-matpro-anthracite hover:bg-matpro-anthracite hover:text-white font-bold rounded-md";

  return (
    <>
      <Button
        type="button"
        data-testid={testId}
        onClick={start}
        disabled={busy}
        variant={variant}
        className={buttonClassName || `${defaultOutline} ${className}`}
      >
        <MessageSquare className="h-4 w-4 mr-2 shrink-0" /> {busy ? "Envoi…" : label}
      </Button>

      {open ? (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-3 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${testId}-title`}
          onClick={() => setOpen(false)}
          data-testid={`${testId}-overlay`}
        >
          <div
            className="relative w-full max-w-lg rounded-xl border border-matpro-border bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Fermer"
              className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-md text-matpro-anthracite-muted hover:bg-zinc-100"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
            <h2 id={`${testId}-title`} className="pr-10 font-heading text-lg font-bold text-matpro-anthracite">
              Envoyer un message au vendeur
            </h2>
            <div className="mt-4 space-y-4">
              <div className="rounded-md border border-matpro-border bg-matpro-bg p-3 text-sm">
                <div className="line-clamp-1 font-semibold text-matpro-anthracite">{listing.title}</div>
                <div className="mt-1 font-heading text-base font-black text-matpro-green">
                  {formatPrice(listing.price)}
                </div>
                <Link
                  to={`/annonce/${listing.id}`}
                  className="mt-2 inline-block text-xs font-semibold text-matpro-green hover:underline"
                >
                  Voir l&apos;annonce
                </Link>
              </div>
              <textarea
                data-testid={testId === "detail-contact" ? "contact-message" : `${testId}-message`}
                rows={5}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full rounded-md border border-matpro-border bg-white px-3 py-2 text-sm text-matpro-anthracite outline-none ring-matpro-green focus:ring-2"
              />
              <Button
                data-testid="contact-send"
                disabled={busy || text.trim().length < 2}
                onClick={submit}
                className="w-full rounded-md bg-matpro-green font-bold text-white hover:bg-matpro-green-dark"
              >
                {busy ? "Envoi…" : "Envoyer le message"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
